/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {Component, NgZone, OnDestroy, OnInit} from '@angular/core';
import {ReportService} from '../../../../services/report.service';
import {ExportProvider, ReportProvider, TemplateProvider} from '../../../../providers';
import {Report, User} from '../../../../model';
import {AppFileData} from '../../../../model/app-file-data';
import {UserService,LogService} from '../../../../services';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
import * as fs from 'wicg-file-system-access';
import { ExportHistory } from 'src/app/model/export.history';

@Component({
  selector: 'page-report-export',
  templateUrl: 'report.export.page.html',
  styleUrls: ['report.export.page.scss']
})
export class ReportExportPage implements OnInit, OnDestroy {
  currentReport: Report;
  private reportSub: any;

  user: User;
  private userSub: any;
  templateId: string;

  chromeAppInstalled: boolean;
  private chromeAppSub: any;

  STAGES = {
    SELECTED: null,
    SENT: null,
    RECEIVED: null,
    WRITE: null,
    RESULT: null
  };

  fileHandle: FileSystemFileHandle;
  allowFileHandleRetry: boolean = false;
  appData: AppFileData;

  exportHistory: ExportHistory[] = [];

  constructor(
    private reportProvider: ReportProvider,
    private reportService: ReportService,
    private exportProvider: ExportProvider,
    private userService: UserService,
    public templateProvider: TemplateProvider,
    private ngZone: NgZone,
    private logService: LogService
  ) {}

  ngOnInit(): void {
    this.reportSub = this.reportService.report$.subscribe(currentReport => {
      if(currentReport){
        this.currentReport = currentReport;

        this.loadExportHistory();
      }
    });

    this.userSub = this.userService.user$.subscribe(u => this.user = u);
    this.chromeAppSub = this.userService.extensionState$.subscribe(extState => this.chromeAppInstalled = extState);
  }

  ngOnDestroy(): void {
    if (this.reportSub) this.reportSub.unsubscribe();
    if (this.userSub) this.userSub.unsubscribe();
    if (this.chromeAppSub) this.chromeAppSub.unsubscribe();
  }

  async requestFile(): Promise<void> {
    try{
      let [fileHandle] = await showOpenFilePicker({
        types: [{description: 'Excel Files', accept: {'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx', '.xlsm']}}]
      });

      this.fileHandle = fileHandle;

      // Verify that User has RW permission to FileHandle
      await this.verifyPermission();

      // Clean Previous Results
      this.STAGES = {
        SELECTED: null,
        SENT: null,
        RECEIVED: null,
        WRITE: null,
        RESULT: null
      };

      let file = await this.fileHandle.getFile();
      let fileReader = new FileReader();
      fileReader.onload = (e: any): any => {
        this.ngZone.run( () => {
          this.STAGES.SELECTED = 'SUCCESS';
          this.allowFileHandleRetry = false;
        });

        let appFileData:AppFileData = new AppFileData();
        appFileData.data = e.target.result;
        appFileData.name = file.name;
        appFileData.size = file.size;

        this.uploadFileToServer(appFileData);
      };
      fileReader.readAsDataURL(file);
    }catch (e) {
      this.logService.info(e);
    }
  }

  private uploadFileToServer(file: AppFileData): void {
    this.ngZone.run( () => {
      this.STAGES.SENT = 'IN PROGRESS';
    });

    this.exportProvider.requestExport(this.currentReport.ID, this.templateId, file)
      .subscribe(data => {
        this.ngZone.run( () => {
          this.STAGES.SENT = 'SUCCESS';
        });

        if (data.success) {
          this.ngZone.run( () => {
            this.STAGES.RECEIVED = 'SUCCESS';
          });

          const appFileData = AppFileData.parse(data.result);
          // noinspection JSIgnoredPromiseFromCall
          this.writeFile(appFileData);
        } else {
          this.ngZone.run( () => {
            this.STAGES.RECEIVED = 'FAILED';
          });

          this.logService.error(data);
        }
      }, error => {
        this.ngZone.run( () => {
          this.STAGES.SENT = 'FAILED';
        });

        this.logService.error(error);
      });
  }

  async writeFile(appData: AppFileData):Promise<void> {
    this.ngZone.run( () => {
      this.STAGES.WRITE = 'IN PROGRESS';
    });

    try {
      let blob = new Blob([this.s2ab(atob(appData.data))], {type: ''});

      const writer = await this.fileHandle.createWritable();
      await writer.write(blob);
      await writer.close();

      this.ngZone.run( () => {
        this.STAGES.WRITE = 'SUCCESS';
      });
    }catch (e) {
      // Retry File Choose
      this.allowFileHandleRetry = true;
      this.appData = appData;
      this.ngZone.run( () => {
        this.STAGES.WRITE = 'FAILED';
      });
      this.logService.info('Native File System API (Write):');
      this.logService.log(e);
    }

    this.exportProvider.setClientState(this.currentReport.ID, appData.history.ID, this.STAGES.WRITE)
      .subscribe(data => {
        if(data.success){
          appData.history.STATE_CLIENT = this.STAGES.WRITE;
          this.exportHistory.push(appData.history);
        }
      }, err => {
        this.logService.error(err);
      })
  }

  async verifyPermission(): Promise<boolean> {
    // Check if permission was already granted. If so, return true.
    if ((await this.fileHandle.queryPermission({mode: 'readwrite'})) === 'granted')
      return true;

    // Request permission. If the user grants permission, return true.
    if ((await this.fileHandle.requestPermission({mode: 'readwrite'})) === 'granted')
      return true;

    // The user didn't grant permission, so return false.
    return false;
  }

  async retryFileChoose():Promise<void> {
    try{
      this.allowFileHandleRetry = false;
      let [fileHandle] = await showOpenFilePicker({
        types: [{description: 'Excel Files', accept: {'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx', '.xlsm']}}]
      });

      this.fileHandle = fileHandle;

      await this.writeFile(this.appData);
    }catch (e) {
      this.logService.info(e);
    }
  }

  s2ab(s): ArrayBuffer {
    let buf = new ArrayBuffer(s.length);
    let view = new Uint8Array(buf);
    for (let i=0; i !== s.length; ++i) view[i] = s.charCodeAt(i) & 0xFF;
    return buf;
  }

  searchTemplate(value: any): void {
    this.templateId = value != null ? value.VALUE.ID : null;
  }


  loadExportHistory(): void {
    this.exportProvider.getHistory(this.currentReport.ID)
      .subscribe(data => {
        if(data.success){
          this.exportHistory = ExportHistory.parseArray(data.result);
        }
      }, err => {
        this.logService.error(err);
      })
  }
  isShowHistory(): boolean {
    return this.user.hasRolePrivilege("EXPORT_STATS_VIEW");
  }
}
